import { BaseService } from './BaseService';
import type { PagedResult } from '../types/tenant';
import type {
  SkillDto,
  SkillDetailDto,
  AgentSkillAssignmentDto,
  SkillFilters,
} from '../types/skill';

/**
 * Skills API: upload bundles (SKILL.md + assets) and manage agent assignments.
 */
export class SkillService extends BaseService {
  async getSkills(filters?: SkillFilters): Promise<PagedResult<SkillDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);
    if (filters?.category) queryParams.append('category', filters.category);
    if (filters?.isActive !== undefined) queryParams.append('isActive', String(filters.isActive));
    if (filters?.providerId) queryParams.append('providerId', filters.providerId);

    const url = `/skills${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<SkillDto>>(url);
  }

  async getSkill(id: string): Promise<SkillDetailDto> {
    return this.get<SkillDetailDto>(`/skills/${id}`);
  }

  /**
   * Upload a zip containing SKILL.md (and optional files). Field name must match API: `files`.
   */
  async createSkillFromZip(file: File, category?: string, providerConfigId?: string): Promise<SkillDetailDto> {
    const form = new FormData();
    form.append('files', file);
    if (category?.trim()) {
      form.append('category', category.trim());
    }
    if (providerConfigId) {
      form.append('providerConfigId', providerConfigId);
    }
    const response = await this.client.post<SkillDetailDto>('/skills', form);
    return response.data;
  }

  async deleteSkill(id: string): Promise<void> {
    await this.delete<void>(`/skills/${id}`);
  }

  async getAgentSkills(agentId: string): Promise<AgentSkillAssignmentDto[]> {
    return this.get<AgentSkillAssignmentDto[]>(`/agents/${agentId}/skills`);
  }

  async assignSkillToAgent(
    agentId: string,
    skillId: string,
    versionNumber?: number | null
  ): Promise<AgentSkillAssignmentDto> {
    return this.post<AgentSkillAssignmentDto>(`/agents/${agentId}/skills`, {
      skill_id: skillId,
      version_number: versionNumber ?? null,
    });
  }

  async unassignSkillFromAgent(agentId: string, assignmentId: string): Promise<void> {
    await this.delete<void>(`/agents/${agentId}/skills/${assignmentId}`);
  }
}

export const skillService = new SkillService();
